import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { History, RefreshCw } from "lucide-react";

/**
 * Audit trail for the Runtime health configuration surface.
 * Rows come from public.admin_audit_logs, written by the
 * audit_admin_table_write() trigger on each of these tables.
 */
const TABLES = [
  { id: "all", label: "All runtime health tables" },
  { id: "frontend_error_alert_recipients", label: "Recipients & channels" },
  { id: "frontend_error_alert_settings", label: "Alert settings" },
  { id: "frontend_error_incidents", label: "Incidents" },
  { id: "frontend_error_regression_routing", label: "Regression routing rules" },
] as const;

const WATCHED = TABLES.filter((t) => t.id !== "all").map((t) => t.id);

type LogRow = {
  id: string;
  created_at: string;
  user_id: string | null;
  action: string;
  target_table: string | null;
  target_id: string | null;
  details: Record<string, unknown> | null;
};

function changedFields(details: Record<string, unknown> | null): string[] {
  const oldRow = (details?.old ?? null) as Record<string, unknown> | null;
  const newRow = (details?.new ?? null) as Record<string, unknown> | null;
  if (!oldRow || !newRow) return [];
  const skip = new Set(["updated_at"]);
  return Object.keys(newRow).filter(
    (k) => !skip.has(k) && JSON.stringify(newRow[k]) !== JSON.stringify(oldRow[k]),
  );
}

export function RuntimeHealthAuditPanel() {
  const { toast } = useToast();
  const [rows, setRows] = useState<LogRow[]>([]);
  const [table, setTable] = useState<(typeof TABLES)[number]["id"]>("all");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from("admin_audit_logs")
      .select("id, created_at, user_id, action, target_table, target_id, details")
      .order("created_at", { ascending: false })
      .limit(100);
    query = table === "all" ? query.in("target_table", WATCHED) : query.eq("target_table", table);
    const { data, error } = await query;
    if (error) {
      toast({ title: "Could not load audit log", description: error.message, variant: "destructive" });
    } else {
      setRows((data ?? []) as unknown as LogRow[]);
    }
    setLoading(false);
  }, [table, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <History className="size-4 text-primary" /> Runtime health change log
          </CardTitle>
          <CardDescription>
            Every recipient, channel, incident, and regression-rule change — who did it, when, and
            exactly which fields moved. Automated (system) writes are excluded by design.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="rh-audit-table" className="sr-only">Filter by table</Label>
          <Select value={table} onValueChange={(v) => setTable(v as typeof table)}>
            <SelectTrigger id="rh-audit-table" className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TABLES.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {rows.length === 0 && !loading && (
          <p className="text-sm text-muted-foreground">No changes recorded yet.</p>
        )}
        {rows.map((row) => {
          const fields = changedFields(row.details);
          return (
            <div key={row.id} className="rounded-md border border-border p-3 text-xs">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="font-mono">{row.action}</Badge>
                <span className="text-muted-foreground">
                  {new Date(row.created_at).toLocaleString()}
                </span>
                <span className="font-mono text-muted-foreground">
                  actor {row.user_id ? row.user_id.slice(0, 8) : "system"}
                </span>
                {row.target_id && (
                  <span className="font-mono text-muted-foreground">row {row.target_id.slice(0, 8)}</span>
                )}
                {fields.length > 0 && <span>changed: {fields.join(", ")}</span>}
                <button
                  className="ml-auto text-primary underline"
                  onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                >
                  {expanded === row.id ? "Hide details" : "Show details"}
                </button>
              </div>
              {expanded === row.id && (
                <pre className="mt-2 max-h-64 overflow-auto rounded bg-muted p-2 font-mono text-[11px] whitespace-pre-wrap">
                  {JSON.stringify(row.details, null, 2)}
                </pre>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export default RuntimeHealthAuditPanel;
